import useLazyResource from 'util/resource/useLazyResource';
import { invalidate } from 'hooks/useSWR';
import useNotify from 'hooks/useNotify';
import conversationApi, { PostConversationMessage, UserConversation } from './conversationApi';
import { conversationMeKey } from './useConversationMe';
import { reloadConversationList } from './useConversationList';
import { reloadConversationMessages } from './useConversationMessages';
import { reloadConversationRequestList } from './useConversationRequestList';
import config from 'config';

export const useConversationActions = () => {
  const notify = useNotify();

  const [createConversation, isCreatingConversation] = useLazyResource(
    async ({
      withUsers,
      message,
      name,
    }: {
      withUsers: UUID[];
      message: PostConversationMessage;
      name?: string;
      onSuccess?: (id: UserConversation) => void;
    }) => {
      return await conversationApi.user.createConversation(withUsers, message, name);
    },
    {
      onSuccess: (result, params) => {
        reloadConversationList();
        // Invalidate messages for the newly created conversation
        if (result?.id) {
          params.onSuccess?.(result);

          reloadConversationMessages(result.id);
        }
      },
      onFailure: () => {
        notify('error', 'Failed to send message');
      },
    },
  );

  const [approveConversationRequest, isApprovingRequest] = useLazyResource(
    async (conversationId: UUID) => {
      const result = await conversationApi.user.approveConversation(conversationId);

      // Invalidate all relevant caches
      reloadConversationList();
      reloadConversationRequestList();
      reloadConversationMessages(conversationId);
      // Invalidate the specific conversation data since its state changed
      invalidate(`${config.COMPANIES_API_URL}/conversations/${conversationId}`);
      return result;
    },
    {
      onSuccess: () => {
        notify('success', 'Conversation request approved');
      },
      onFailure: () => {
        notify('error', 'Failed to approve conversation request');
      },
    },
  );

  const [rejectConversationRequest, isRejectingRequest] = useLazyResource(
    async (conversationId: UUID) => {
      await conversationApi.user.rejectConversation(conversationId);

      reloadConversationList();
      reloadConversationRequestList();
      return conversationId;
    },
    {
      onSuccess: () => {
        notify('success', 'Conversation request rejected');
      },
      onFailure: () => {
        notify('error', 'Failed to reject conversation request');
      },
    },
  );

  const [sendMessage, isSendingMessage] = useLazyResource(
    async ({ conversationId, message }: { conversationId: UUID; message: PostConversationMessage }) => {
      const result = await conversationApi.user.postMessage(conversationId, message);

      invalidate(conversationMeKey);
      reloadConversationList();
      reloadConversationMessages(conversationId);
      return result;
    },
    {
      onSuccess: () => {},
      onFailure: () => {
        notify('error', 'Failed to send message');
      },
    },
  );

  const [sendCompanyMessage, isSendingCompanyMessage] = useLazyResource(
    async ({ companyId, message }: { companyId: number; message: PostConversationMessage }) => {
      const result = await conversationApi.user.single.reply(companyId, message);
      invalidate(conversationMeKey);
      reloadConversationList();
      return result;
    },
    {
      onSuccess: () => {},
      onFailure: () => {
        notify('error', 'Failed to send message');
      },
    },
  );

  return {
    createConversation,
    isCreatingConversation,
    approveConversationRequest,
    isApprovingRequest,
    rejectConversationRequest,
    isRejectingRequest,
    sendMessage,
    isSendingMessage,
    sendCompanyMessage,
    isSendingCompanyMessage,
  };
};
